import { Checkbox, CheckboxCard } from '@1trading/react/components/ui/checkbox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Textarea } from '@1trading/react/components/ui/textarea';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { EDD_CATEGORIES, EddCategory, PROHIBITED_CATEGORIES, ProhibitedCategory } from '@/constants/institution';
import type { InstitutionKycCddOrDefaults } from '@/features/institution/kyc-cdd/schema';

const InstitutionKycCddFieldsetCategoriesLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.kycCdd.categories.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.kycCdd.categories.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionKycCddFieldsetCategoriesLegend.displayName = 'InstitutionKycCddFieldsetCategoriesLegend';

const InstitutionKycCddFieldsetCategories = () => {
  const form = useFormContext<InstitutionKycCddOrDefaults>();
  const { t } = useTranslation();
  const [eddCategories, prohibitedCategories] = form.watch(['eddCategories', 'prohibitedCategories']);

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="eddCategories"
        render={() => (
          <FormItem>
            <FormLabel>{t('institutionDetails.kycCdd.categories.eddCategories')}*</FormLabel>
            <FormControl aria-label={t('institutionDetails.kycCdd.categories.eddCategories')}>
              {/* biome-ignore lint/a11y/useSemanticElements: Makeshift checkboxgroup since it's not a native role */}
              <div className="space-y-4" role="group">
                {EDD_CATEGORIES.map((option) => (
                  <FormField
                    key={option}
                    control={form.control}
                    name="eddCategories"
                    render={({ field }) => (
                      <FormItem>
                        <CheckboxCard variant="outline">
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(option)}
                              variant="cards"
                              onCheckedChange={(checked) => field.onChange(checked ? [...(field.value ?? []), option] : field.value?.filter((value) => value !== option))}
                            />
                          </FormControl>
                          <FormLabel contained>{t(`institutionDetails.kycCdd.categories.eddCategoriesOptions.${option}`)}</FormLabel>
                        </CheckboxCard>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {eddCategories?.includes(EddCategory.OTHER) ? (
        <FormField
          control={form.control}
          name="eddCategoriesOther"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.kycCdd.categories.eddCategoriesOther')}*</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder={t('institutionDetails.kycCdd.placeholders.eddCategoriesOther')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
      <FormField
        control={form.control}
        name="prohibitedCategories"
        render={() => (
          <FormItem>
            <FormLabel>{t('institutionDetails.kycCdd.categories.prohibitedCategories')}*</FormLabel>
            <FormControl aria-label={t('institutionDetails.kycCdd.categories.prohibitedCategories')}>
              {/* biome-ignore lint/a11y/useSemanticElements: Makeshift checkboxgroup since it's not a native role */}
              <div className="space-y-4" role="group">
                {PROHIBITED_CATEGORIES.map((option) => (
                  <FormField
                    key={option}
                    control={form.control}
                    name="prohibitedCategories"
                    render={({ field }) => (
                      <FormItem>
                        <CheckboxCard variant="outline">
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(option)}
                              variant="cards"
                              onCheckedChange={(checked) => field.onChange(checked ? [...(field.value ?? []), option] : field.value?.filter((value) => value !== option))}
                            />
                          </FormControl>
                          <FormLabel contained>{t(`institutionDetails.kycCdd.categories.prohibitedCategoriesOptions.${option}`)}</FormLabel>
                        </CheckboxCard>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {prohibitedCategories?.includes(ProhibitedCategory.OTHER) ? (
        <FormField
          control={form.control}
          name="prohibitedCategoriesOther"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('institutionDetails.kycCdd.categories.prohibitedCategoriesOther')}*</FormLabel>
              <FormControl>
                <Textarea {...field} placeholder={t('institutionDetails.kycCdd.placeholders.prohibitedCategoriesOther')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </div>
  );
};
InstitutionKycCddFieldsetCategories.displayName = 'InstitutionKycCddFieldsetCategories';

export { InstitutionKycCddFieldsetCategories, InstitutionKycCddFieldsetCategoriesLegend };
